import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Lightbulb, Send, MessageSquare, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function SuggestionsSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user's active cohort
  const { data: activeCohort } = useQuery({
    queryKey: ["user-active-cohort", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("cohort_participants")
        .select("cohort_id, cohorts(name)")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  // Fetch previous NPS responses
  const { data: responses = [] } = useQuery({
    queryKey: ["nps-responses", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("nps_responses")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const handleSubmit = async () => {
    if (score === null || !user) {
      toast.error("Por favor selecciona una puntuación");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("nps_responses").insert({
        user_id: user.id,
        score,
        comment: comment.trim() || null,
        cohort_id: activeCohort?.cohort_id || null,
      });
      if (error) throw error;

      toast.success("¡Gracias por tu feedback!");
      setScore(null);
      setComment("");
      queryClient.invalidateQueries({ queryKey: ["nps-responses", user.id] });
    } catch (err: any) {
      toast.error("Error al enviar", { description: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getScoreColor = (value: number, selected: number | null) => {
    if (selected !== value) return "bg-muted hover:bg-muted/80 text-muted-foreground";
    if (value <= 6) return "bg-destructive text-destructive-foreground";
    if (value <= 8) return "bg-amber-500 text-white";
    return "bg-emerald-500 text-white";
  };

  const getScoreLabel = (s: number) => {
    if (s <= 6) return "Detractor";
    if (s <= 8) return "Pasivo";
    return "Promotor";
  };

  const getScoreBadgeVariant = (s: number) => {
    if (s <= 6) return "text-destructive bg-destructive/10 border-destructive/30";
    if (s <= 8) return "text-amber-500 bg-amber-500/10 border-amber-500/30";
    return "text-emerald-500 bg-emerald-500/10 border-emerald-500/30";
  };

  return (
    <div className="space-y-6">
      {/* NPS Form */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-primary" />
            Califica tu experiencia
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            ¿Qué tan probable es que recomiendes esta plataforma a un colega?
          </p>

          {/* Score selector */}
          <div className="space-y-3">
            <div className="flex justify-between text-xs text-muted-foreground px-1">
              <span>Nada probable</span>
              <span>Muy probable</span>
            </div>
            <div className="grid grid-cols-11 gap-1">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <button
                  key={value}
                  onClick={() => setScore(value)}
                  className={cn(
                    "h-10 rounded-lg font-semibold text-sm transition-all duration-200",
                    "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    getScoreColor(value, score),
                    score === value && "ring-2 ring-ring ring-offset-2 scale-110"
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
            {score !== null && (
              <p className={cn("text-center font-medium text-sm", 
                score <= 6 ? "text-destructive" : score <= 8 ? "text-amber-500" : "text-emerald-500"
              )}>
                {getScoreLabel(score)} ({score}/10)
              </p>
            )}
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Sugerencias o comentarios <span className="text-muted-foreground">(opcional)</span>
            </label>
            <Textarea
              placeholder="¿Qué podríamos mejorar? ¿Qué te gustaría ver en la plataforma?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="resize-none"
              maxLength={1000}
            />
          </div>

          <Button onClick={handleSubmit} disabled={isSubmitting || score === null} className="gap-2">
            <Send className="w-4 h-4" />
            {isSubmitting ? "Enviando..." : "Enviar feedback"}
          </Button>
        </CardContent>
      </Card>

      {/* History */}
      {responses.length > 0 && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MessageSquare className="w-5 h-5 text-primary" />
              Tu historial de feedback ({responses.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {responses.map((r, i) => (
              <div key={r.id}>
                {i > 0 && <Separator className="mb-4" />}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge className={cn("text-xs", getScoreBadgeVariant(r.score))}>
                        {r.score}/10 · {getScoreLabel(r.score)}
                      </Badge>
                    </div>
                    {r.comment && (
                      <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                    <Calendar className="w-3 h-3" />
                    {format(new Date(r.created_at), "d MMM yyyy", { locale: es })}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
